import type { Engine, FontSpec, Profile } from './types';

const ENGINES = new Set(['chromium', 'webkit']);
const CLASSES = new Set(['mobile', 'tablet', 'desktop']);
const STATUSES = new Set(['verified', 'assumed', 'experimental']);
const KEBAB = /^[a-z0-9]+(-[a-z0-9]+)*$/;

function fail(msg: string): never {
  throw new Error(`Invalid profile: ${msg}`);
}

function checkFont(where: string, font: unknown) {
  if (typeof font !== 'string' || !font.trim()) fail(`${where} must be a canvas font string`);
  if (!/(^|\s)\d+(\.\d+)?px(\/|\s)/.test(font + ' ')) fail(`${where} must use a px font size (got ${JSON.stringify(font)})`);
  if (/system-ui|-apple-system|BlinkMacSystemFont/i.test(font)) fail(`${where} must name a font, not system-ui (got ${JSON.stringify(font)})`);
}

function positive(where: string, v: unknown) {
  if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) fail(`${where} must be a positive number`);
}

/** Validate every profile; throws naming the profile and field. Returns profiles in `order` (then the rest). */
export function validateProfiles(profiles: Profile[], fonts: FontSpec[], order: string[] = []): Profile[] {
  const fontIds = new Set<string>();
  for (const f of fonts) {
    if (!f.id || !KEBAB.test(f.id)) throw new Error(`Invalid font: id ${JSON.stringify(f.id)} must be kebab-case`);
    if (!f.family) throw new Error(`Invalid font: ${f.id}.family is required`);
    if (!/^https?:\/\//.test(f.url)) throw new Error(`Invalid font: ${f.id}.url must be an http(s) URL`);
    fontIds.add(f.id);
  }
  const ids = new Set<string>();
  for (const p of profiles) {
    const id = p?.id;
    if (typeof id !== 'string' || !KEBAB.test(id)) fail(`id ${JSON.stringify(id)} must be kebab-case`);
    if (ids.has(id)) fail(`duplicate id ${id}`);
    ids.add(id);
    if (typeof p.label !== 'string' || !p.label) fail(`${id}.label is required`);
    if (!ENGINES.has(p.engine)) fail(`${id}.engine must be chromium or webkit`);
    if (!CLASSES.has(p.deviceClass)) fail(`${id}.deviceClass must be mobile, tablet or desktop`);
    if (!STATUSES.has(p.status)) fail(`${id}.status must be verified, assumed or experimental`);
    positive(`${id}.viewportWidth`, p.viewportWidth);
    for (const field of ['title', 'description'] as const) {
      const l = p[field];
      if (!l || typeof l !== 'object') fail(`${id}.${field} is required`);
      checkFont(`${id}.${field}.font`, l.font);
      if (l.boldFont !== undefined) checkFont(`${id}.${field}.boldFont`, l.boldFont);
      positive(`${id}.${field}.width`, l.width);
      positive(`${id}.${field}.lineHeight`, l.lineHeight);
      if (!Number.isInteger(l.maxLines) || l.maxLines < 1) fail(`${id}.${field}.maxLines must be a positive integer`);
    }
    if (!Array.isArray(p.fonts)) fail(`${id}.fonts must be an array`);
    for (const f of p.fonts) if (!fontIds.has(f)) fail(`${id}.fonts has unknown font id ${JSON.stringify(f)}`);
  }
  for (const p of profiles) {
    if (p.fontTwin !== undefined && !ids.has(p.fontTwin)) fail(`${p.id}.fontTwin has unknown profile id ${JSON.stringify(p.fontTwin)}`);
    if (p.fontTwin === p.id) fail(`${p.id}.fontTwin cannot be itself`);
  }
  for (const o of order) if (!ids.has(o)) fail(`index.json lists unknown profile ${o}`);
  const rank = new Map(order.map((id, i) => [id, i]));
  return [...profiles].sort((a, b) => (rank.get(a.id) ?? 1e9) - (rank.get(b.id) ?? 1e9));
}

export class ProfileSet {
  readonly list: Profile[];
  readonly byId: Map<string, Profile>;
  readonly fonts: FontSpec[];
  readonly fontsById: Map<string, FontSpec>;

  constructor(profiles: Profile[], fonts: FontSpec[], order: string[] = []) {
    this.list = validateProfiles(profiles, fonts, order);
    this.byId = new Map(this.list.map(p => [p.id, p]));
    this.fonts = fonts;
    this.fontsById = new Map(fonts.map(f => [f.id, f]));
  }

  get(id: string): Profile {
    const p = this.byId.get(id);
    if (!p) throw new Error(`Unknown profile "${id}". Known: ${this.list.map(p => p.id).join(', ')}`);
    return p;
  }

  defaults(configDefaults: string[] = []): Profile[] {
    if (configDefaults.length) return configDefaults.map(id => this.get(id));
    return this.list.filter(p => p.default);
  }

  /** Resolve a selector: `default`, `all`, or comma-separated ids, which may be mixed (`default,iphone-se`). */
  select(selector: string | string[] | undefined, configDefaults: string[] = []): Profile[] {
    const parts = (Array.isArray(selector) ? selector : (selector ?? 'default').split(','))
      .map(s => s.trim())
      .filter(Boolean);
    const chosen = new Set<string>();
    for (const part of parts.length ? parts : ['default']) {
      if (part === 'default') for (const p of this.defaults(configDefaults)) chosen.add(p.id);
      else if (part === 'all') for (const p of this.list) chosen.add(p.id);
      else chosen.add(this.get(part).id);
    }
    return this.list.filter(p => chosen.has(p.id));
  }

  /** Font ids a profile needs in its engine: its own fonts plus its twin's. */
  fontsFor(profile: Profile): FontSpec[] {
    const ids = new Set(profile.fonts);
    if (profile.fontTwin) for (const f of this.get(profile.fontTwin).fonts) ids.add(f);
    return [...ids].map(id => this.fontsById.get(id)!).filter(Boolean);
  }

  engines(profiles: Profile[]): Engine[] {
    const set = new Set(profiles.map(p => p.engine));
    return (['chromium', 'webkit'] as Engine[]).filter(e => set.has(e));
  }
}

/** Family names referenced by a canvas font string, e.g. `342 22px "Google Sans Flex"` → ["Google Sans Flex"]. */
export function fontFamilies(font: string): string[] {
  const m = font.match(/\d(?:\.\d+)?px(?:\/\S+)?\s+(.+)$/);
  if (!m) return [];
  return m[1].split(',').map(s => s.trim().replace(/^["']|["']$/g, '')).filter(Boolean);
}
